import { Component, OnInit, inject, signal, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterLink } from '@angular/router';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { ChartModule } from 'primeng/chart';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, finalize, forkJoin, of } from 'rxjs';
import { PropertyAdminService } from '../../core/services/property-admin.service';
import { DocumentAdminService } from '../../core/services/document-admin.service';
import { PlanAdminService } from '../../core/services/plan-admin.service';
import { PropertyResponse } from '../../core/models';

// Taille de l'échantillon utilisé pour les graphiques de répartition du tableau
// de bord. Il n'existe pas d'endpoint d'agrégation côté backend : les graphiques
// sont donc calculés côté client à partir d'un instantané (snapshot) des biens.
const SNAPSHOT_SIZE = 200;

const STATUS_LABELS: Record<string, string> = {
  PENDING_REVIEW: 'En attente', PUBLISHED: 'Publié', REJECTED: 'Rejeté', DRAFT: 'Brouillon'
};
const STATUS_COLORS: Record<string, string> = {
  PENDING_REVIEW: '#f59e0b', PUBLISHED: '#16a34a', REJECTED: '#ef4444', DRAFT: '#94a3b8'
};
const TYPE_LABELS: Record<string, string> = {
  APPARTEMENT: 'Appartement', MAISON: 'Maison', VILLA: 'Villa',
  TERRAIN: 'Terrain', BUREAU: 'Bureau', LOCAL_COMMERCIAL: 'Local commercial', ENTREPOT: 'Entrepôt', HOTEL: 'Hôtel'
};

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, RouterLink, CardModule, ButtonModule, TagModule, TableModule,
    ChartModule, ProgressSpinnerModule, TooltipModule],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss'
})
export class DashboardComponent implements OnInit {
  private propSvc = inject(PropertyAdminService);
  private docSvc  = inject(DocumentAdminService);
  private planSvc = inject(PlanAdminService);
  private platformId = inject(PLATFORM_ID);

  loading = signal(true);
  today = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  pendingProperties = signal(0);
  pendingDocuments  = signal(0);
  plansCount        = signal(0);
  totalProperties   = signal(0);
  recentProperties  = signal<PropertyResponse[]>([]);

  snapshotCount = signal(0);

  statusChartData: any = null;
  statusChartOptions: any = null;
  typeChartData: any = null;
  typeChartOptions: any = null;

  ngOnInit() {
    forkJoin({
      pending:    this.propSvc.getPendingProperties(0, 5).pipe(catchError(() => of(null))),
      all:        this.propSvc.getAllProperties(0, SNAPSHOT_SIZE).pipe(catchError(() => of(null))),
      documents:  this.docSvc.getPendingDocuments().pipe(catchError(() => of([]))),
      plans:      this.planSvc.getPlans().pipe(catchError(() => of([])))
    }).pipe(
      finalize(() => this.loading.set(false))
    ).subscribe(({ pending, all, documents, plans }) => {
      this.pendingProperties.set(pending?.totalElements ?? 0);
      this.recentProperties.set(pending?.content?.slice(0, 5) ?? []);
      this.pendingDocuments.set(documents.length);
      this.plansCount.set(plans.length);

      this.totalProperties.set(all?.totalElements ?? 0);
      this.snapshotCount.set(all?.content?.length ?? 0);

      if (isPlatformBrowser(this.platformId)) {
        this.buildCharts(all?.content ?? []);
      }
    });
  }

  private buildCharts(properties: PropertyResponse[]) {
    // ── Répartition par statut (doughnut) ──────────────────────────────────
    const byStatus = new Map<string, number>();
    for (const p of properties) byStatus.set(p.status, (byStatus.get(p.status) ?? 0) + 1);
    const statusKeys = [...byStatus.keys()];

    this.statusChartData = {
      labels: statusKeys.map(k => STATUS_LABELS[k] ?? k),
      datasets: [{
        data: statusKeys.map(k => byStatus.get(k)),
        backgroundColor: statusKeys.map(k => STATUS_COLORS[k] ?? '#64748b'),
        borderWidth: 0
      }]
    };
    this.statusChartOptions = {
      plugins: {
        legend: { position: 'bottom', labels: { usePointStyle: true, padding: 16, font: { size: 12 } } }
      },
      cutout: '68%',
      maintainAspectRatio: false
    };

    // ── Répartition par type (barres) ──────────────────────────────────────
    const byType = new Map<string, number>();
    for (const p of properties) byType.set(p.type, (byType.get(p.type) ?? 0) + 1);
    const typeEntries = [...byType.entries()].sort((a, b) => b[1] - a[1]);

    this.typeChartData = {
      labels: typeEntries.map(([k]) => TYPE_LABELS[k] ?? k),
      datasets: [{
        label: 'Biens',
        data: typeEntries.map(([, v]) => v),
        backgroundColor: '#0D47A1',
        borderRadius: 6,
        maxBarThickness: 38
      }]
    };
    this.typeChartOptions = {
      plugins: { legend: { display: false } },
      scales: {
        x: { grid: { display: false } },
        y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: '#f1f5f9' } }
      },
      maintainAspectRatio: false
    };
  }

  statusSeverity(s: string): 'warn' | 'success' | 'danger' | 'secondary' {
    if (s === 'PENDING_REVIEW') return 'warn';
    if (s === 'PUBLISHED') return 'success';
    if (s === 'REJECTED') return 'danger';
    return 'secondary';
  }

  statusLabel(s: string) { return STATUS_LABELS[s] ?? s; }
  typeLabel(t: string)   { return TYPE_LABELS[t] ?? t; }
}
